import {
  CoreApi,
  ErrorMessages,
  Position,
  Product,
  ProductType
} from '@inter-ikea-storageone/range';
import { ComponentIds } from '@inter-ikea-core/core';
import { PlacementResult } from '@inter-ikea-core/core';
import { BehaviorParameters } from 'rangeConfig/@types';
import { hideNoFramesInSceneErrorMessage } from 'rangeConfig/shared/rules/hideMenuRules';

function handlePlacementResult(coreApi: CoreApi, placementResult?: PlacementResult | null) {
  if (placementResult && !placementResult.valid) {
    if (placementResult.errors && placementResult.errors.length > 0) {
      const placementAttemptErrorMessage = !hideNoFramesInSceneErrorMessage(coreApi)
        ? ErrorMessages.NoFramesInScene
        : ErrorMessages.ItemWontFit;
      coreApi.emit.onPlacementAttempt(placementAttemptErrorMessage);
      coreApi.entities.disposeCollidingEntities(placementResult.errors);
    }
  }
}

function addAccessoryToScene(
  coreProductIds: string[],
  coreApi: CoreApi,
  position: Position,
  products: Product[]
): PlacementResult | null {
  const widestCoreProduct = coreProductIds.reduce(
    (product: any, coreProductId) => {
      const coreProduct = products.find(p => p.coreProductId === coreProductId);

      if (!!coreProduct && coreProduct.dimensions.width > product.dimensions.width) {
        product = coreProduct;
      }

      return product;
    },
    { dimensions: { width: 0 } }
  );

  const placementResult = coreApi.products.add(widestCoreProduct.coreProductId, position);
  return placementResult;
}

function addOrganizerToScene(
  coreProductIds: string[],
  coreApi: CoreApi,
  position: Position,
  products: Product[]
): PlacementResult {
  const initialProduct = products.find(p => coreProductIds.includes(p.coreProductId));
  const widestCoreProduct = coreProductIds.reduce((product: any, coreProductId) => {
    const coreProduct = products.find(p => p.coreProductId === coreProductId);

    if (!!coreProduct && coreProduct.dimensions.width > product.dimensions.width) {
      product = coreProduct;
    }

    return product;
  }, initialProduct);

  if (position) {
    const drawerOrginizerDroppedOn = coreApi.entities.getEntityClosestToPosition(position, [
      ComponentIds.DRAWER_COMPONENT_ID
    ]);

    if (drawerOrginizerDroppedOn) {
      return coreApi.products.addToTarget(
        widestCoreProduct.coreProductId,
        drawerOrginizerDroppedOn
      );
    }
  }

  return coreApi.products.add(widestCoreProduct.coreProductId, position);
}

const addAccessory = ({
  coreApi,
  rootState: {
    products: { inMarket: products }
  },
  props
}: BehaviorParameters) => {
  const currentProduct = products.find(
    product => product.coreProductId === props.coreProductIds[0]
  );

  if (
    currentProduct?.type === ProductType.PAX_SKUBB_STORAGE_CASES ||
    currentProduct?.type === ProductType.PAX_HEMMAFIXARE_STORAGE_CASE ||
    currentProduct?.type === ProductType.PAX_HEMMAFIXARE_STORAGE_CASE_HIGH ||
    currentProduct?.type === ProductType.PAX_SKUBB_SHOE_BOXES ||
    currentProduct?.type === ProductType.PAX_SKUBB_STORAGE_BOXES ||
    currentProduct?.type === ProductType.PAX_SKUBB_STORAGE_BOXES_W_COMP ||
    currentProduct?.type === ProductType.PAX_SKUBB_STORAGE_CASE_WRAP ||
    currentProduct?.type === ProductType.PAX_HEMMAFIXARE_SHOE_BOX ||
    currentProduct?.type === ProductType.PAX_SKUBB_HANGING_STORAGE
  ) {
    handlePlacementResult(
      coreApi,
      addAccessoryToScene(props.coreProductIds, coreApi, props.position, products)
    );
  } else {
    handlePlacementResult(
      coreApi,
      addOrganizerToScene(props.coreProductIds, coreApi, props.position, products)
    );
  }
};

export default addAccessory;
